/**
 * AI Assistant — the post-login home screen.
 *
 * Layout is a three-column app shell: conversation list, the thread, and an
 * optional workspace panel. The previous version was a single centred column
 * with the greeting and two navigation cards permanently pinned above the
 * messages, so the conversation was squeezed into whatever was left and the
 * greeting still sat there on the twentieth turn. The greeting is now the empty
 * state and nothing else, which is where a greeting belongs.
 *
 * Everything stateful lives in `store/useAssistantStore.js`; every request goes
 * through `hooks/useAssistantHook.js`. This file is composition plus the
 * page-level keyboard shortcuts.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useI18n } from '../../i18n';
import { useSwal } from '../../components/Notifications/swal/SwalCom';
import AssistantComposer from './components/AssistantComposer';
import AssistantDropZone from './components/AssistantDropZone';
import AssistantSidebar from './components/AssistantSidebar';
import AssistantThread from './components/AssistantThread';
import AssistantTopBar from './components/AssistantTopBar';
import AssistantWelcome from './components/AssistantWelcome';
import ShortcutsDialog from './components/ShortcutsDialog';
import WorkspaceContextPanel from './components/WorkspaceContextPanel';
import useAssistantHook from './hooks/useAssistantHook';
import useDatasetBuild from './hooks/useDatasetBuild';
import { readIntent } from './utils/datasetIntent';
import useAssistantStore from './store/useAssistantStore';
import { copyConversation, downloadConversation } from './utils/exportConversation';
import './styles/assistant.css';
import { labelingRouteFor, modelTypeOf, toLabelingState } from '../../utils/labelingTarget';

/** True when focus is somewhere the user is typing, so shortcuts must not fire. */
const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

const Assistant = () => {
  const { t } = useI18n();
  const { swalSuccess, swalFail, swalConfirm } = useSwal();

  const {
    threads,
    activeThread,
    activeThreadId,
    messages,
    draft,
    error,
    isStreaming,
    workspace,
    workspaceLoading,
    status,
    includeContext,
    send,
    stop,
    regenerate,
    editAndResend,
    retry,
    newThread,
    setActiveDraft,
    loadWorkspace,
  } = useAssistantHook();

  const {
    attachment,
    attach,
    remove: removeAttachment,
    startInterview,
    answer: answerInterview,
    answerTyped,
    reopen: reopenInterview,
    confirm: confirmInterview,
    queueTrainingFor,
    activeInterview,
    run: runBuild,
    cancel: cancelBuild,
    renameClasses,
    runs,
    isHeld,
  } = useDatasetBuild();

  const sidebarOpen = useAssistantStore((state) => state.sidebarOpen);
  const sidebarCollapsed = useAssistantStore((state) => state.sidebarCollapsed);
  const closeSidebar = useAssistantStore((state) => state.closeSidebar);
  const shortcutsOpen = useAssistantStore((state) => state.shortcutsOpen);
  const setShortcutsOpen = useAssistantStore((state) => state.setShortcutsOpen);
  const contextPanelOpen = useAssistantStore((state) => state.contextPanelOpen);
  const setContextPanelOpen = useAssistantStore((state) => state.setContextPanelOpen);
  const setIncludeContext = useAssistantStore((state) => state.setIncludeContext);
  const editingMessageId = useAssistantStore((state) => state.editingMessageId);
  const setEditingMessageId = useAssistantStore((state) => state.setEditingMessageId);
  const setError = useAssistantStore((state) => state.setError);

  const hasMessages = messages.length > 0;

  // ---- shortcuts -----------------------------------------------------------
  useEffect(() => {
    const onKeyDown = (event) => {
      const meta = event.metaKey || event.ctrlKey;

      if (meta && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        newThread();
        return;
      }
      if (meta && event.key === '/') {
        event.preventDefault();
        document.querySelector('.lf-composer__input')?.focus();
        return;
      }
      if (event.key === 'Escape') {
        // A running import is the more urgent thing to stop, and unlike a
        // stream it is creating real rows while it runs.
        const running = messages.find(
          (message) => message.kind === 'build' && message.run?.status === 'running',
        );
        if (running) {
          event.preventDefault();
          cancelBuild(running.id);
          return;
        }
        if (isStreaming) {
          event.preventDefault();
          stop();
          return;
        }
      }
      // Bare-key shortcuts must never steal a character from a text field.
      if (event.key === '?' && !isTypingTarget(event.target) && !shortcutsOpen) {
        event.preventDefault();
        setShortcutsOpen(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cancelBuild, isStreaming, messages, newThread, setShortcutsOpen, shortcutsOpen, stop]);

  // ---- actions -------------------------------------------------------------

  /**
   * One entry point for the composer.
   *
   * With a dataset staged there are two outcomes, and the difference is whether
   * the message reads as an INSTRUCTION. "build a model from this" produces a
   * plan card; "what is in here?" is a question and goes to the chat — with the
   * archive's manifest prefixed to it, visibly, so the model can actually
   * answer and the user can see exactly what was shared.
   */
  const submit = useCallback(
    (text) => {
      setContextPanelOpen(false);

      // An interview waiting for an answer takes the message first — the reply
      // is parsed against THAT question's domain only, never the global intent
      // parser, so a wizard cannot trap the user inside itself.
      if (activeInterview && answerTyped(activeInterview, text)) {
        setActiveDraft('');
        return;
      }

      const ready = attachment && attachment.status === 'ready';
      if (ready) {
        /*
         * With a dataset attached, anything that is not a plain question opens
         * an interview. This is the fix for the reported bug: the old code fell
         * through to the chat here, which answered "go and do it in Project
         * Management". No verb list is ever complete, so the fallback must be
         * to ASK.
         */
        const intent = readIntent(text);
        if (intent.intent !== 'question') {
          startInterview(text);
          return;
        }
      }
      if (ready) {
        const context = t('assistant.attach.context', {
          name: attachment.fileName,
          images: attachment.images,
          folders: attachment.folders,
          layout:
            attachment.layout === 'folder_per_class'
              ? t('assistant.attach.layoutFolders')
              : t('assistant.attach.layoutFlat'),
        });
        send(`${context}\n\n${text}`.trim());
        removeAttachment();
        return;
      }
      send(text);
    },
    [activeInterview, answerTyped, attachment, removeAttachment, send,
     setActiveDraft, setContextPanelOpen, startInterview, t],
  );

  /** Send a prompt from a suggestion card or a workspace row. */
  const ask = useCallback(
    (prompt) => {
      setContextPanelOpen(false);
      send(prompt);
    },
    [send, setContextPanelOpen],
  );

  /**
   * Rewriting the conversation does not undo what a build created.
   *
   * `regenerate` and `editAndResend` both truncate the message list, which
   * would take a card with it while its project, model and version still exist
   * in Mongo — and neither create endpoint is idempotent, so re-running makes a
   * second set.
   */
  const committed = useMemo(
    () => messages.some((message) => message.kind === 'build' && message.run?.projectId),
    [messages],
  );

  const confirmDestructive = useCallback(
    () =>
      new Promise((resolve) => {
        if (!committed) {
          resolve(true);
          return;
        }
        // swalConfirm hands its result to a callback and returns undefined, so
        // awaiting it directly would resolve immediately and always proceed.
        swalConfirm(t('assistant.build.guard.title'), t('assistant.build.guard.body'), (result) =>
          resolve(!!(result && result.isConfirmed)),
        );
      }),
    [committed, swalConfirm, t],
  );

  const guardedRegenerate = useCallback(async () => {
    if (await confirmDestructive()) regenerate();
  }, [confirmDestructive, regenerate]);

  const guardedEdit = useCallback(
    async (messageId, text) => {
      if (await confirmDestructive()) editAndResend(messageId, text);
    },
    [confirmDestructive, editAndResend],
  );

  /** One-tap prompts, offered once a dataset is staged. */
  const seeds = useMemo(() => {
    if (!attachment || attachment.status !== 'ready') return [];
    const list = [];
    if (attachment.layout === 'folder_per_class') {
      list.push({
        id: 'folders', icon: 'fa-folder-open',
        label: t('assistant.attach.seed.foldersLabel'),
        prompt: t('assistant.attach.seed.foldersPrompt'),
      });
    } else {
      list.push({
        id: 'split', icon: 'fa-object-group',
        label: t('assistant.attach.seed.splitLabel'),
        prompt: t('assistant.attach.seed.splitPrompt'),
      });
    }
    list.push({
      id: 'unlabeled', icon: 'fa-images',
      label: t('assistant.attach.seed.unlabeledLabel'),
      prompt: t('assistant.attach.seed.unlabeledPrompt'),
    });
    list.push({
      id: 'ask', icon: 'fa-lightbulb',
      label: t('assistant.attach.seed.askLabel'),
      prompt: t('assistant.attach.seed.askPrompt'),
    });
    return list;
  }, [attachment, t]);

  const copyText = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (copyError) {
      // Clipboard access needs a secure context; say so rather than silently
      // doing nothing, because the button otherwise looks broken.
      console.warn('Copy failed', copyError);
      return false;
    }
  }, []);

  const userName = (() => {
    try {
      return localStorage.getItem('name') || 'You';
    } catch (storageError) {
      return 'You';
    }
  })();

  const exportThread = useCallback(() => {
    const ok = downloadConversation(activeThread, { userName });
    if (!ok) swalFail(t('assistant.export.failTitle'), t('assistant.export.failBody'));
  }, [activeThread, swalFail, t, userName]);

  const copyAll = useCallback(async () => {
    const ok = await copyConversation(activeThread, { userName });
    if (ok) swalSuccess(t('assistant.export.copiedTitle'), t('assistant.export.copiedBody'));
    else swalFail(t('assistant.export.failTitle'), t('assistant.export.failBody'));
  }, [activeThread, swalFail, swalSuccess, t, userName]);

  /** ↑ in an empty composer edits the previous question. */
  const editLast = useCallback(() => {
    if (isStreaming) return;
    const lastUser = [...messages].reverse().find((message) => message.role === 'user');
    if (lastUser) setEditingMessageId(lastUser.id);
  }, [isStreaming, messages, setEditingMessageId]);

  return (
    <div className="lf-assistant lf-fill-viewport">
      {/* Mobile drawer backdrop. Desktop collapse is a width change instead. */}
      {sidebarOpen && (
        <div
          className="lf-assistant__backdrop lf-only-md"
          onMouseDown={closeSidebar}
          aria-hidden="true"
        />
      )}

      <div
        className={`lf-assistant__sidebar${sidebarOpen ? ' is-open' : ''}${
          sidebarCollapsed ? ' is-collapsed' : ''
        }`}
      >
        <AssistantSidebar
          threads={threads}
          activeThreadId={activeThreadId}
          onNewChat={newThread}
        />
      </div>

      <main className="lf-assistant__main">
        <AssistantTopBar
          thread={activeThread}
          status={status}
          workspace={workspace}
          onExport={exportThread}
          onCopyAll={copyAll}
          onNewChat={newThread}
        />

        {error && (
          <div className="lf-assistant__banner" role="alert">
            <i className="fa fa-exclamation-triangle" aria-hidden="true" />
            <span>{error}</span>
            <button type="button" onClick={retry} disabled={isStreaming}>
              <i className="fa fa-sync" aria-hidden="true" /> {t('assistant.message.retry')}
            </button>
            <button
              type="button"
              className="lf-assistant__banner-close"
              onClick={() => setError(null)}
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>
        )}

        {/*
          * Dropping is scoped to the conversation only — not the sidebar, not
          * the workspace panel — so a drop always means one thing.
          */}
        <AssistantDropZone onFiles={attach} disabled={isStreaming}>
          <div className="lf-assistant__body">
            {hasMessages ? (
              <AssistantThread
                // Remount per conversation. The thread holds its own "is the user
                // following the bottom?" flag, and without a key that flag leaks
                // across a switch — scroll up in one conversation, open another,
                // and it opens halfway up instead of at the newest message.
                key={activeThreadId}
                messages={messages}
                isStreaming={isStreaming}
                editingMessageId={editingMessageId}
                onCopy={copyText}
                onRegenerate={guardedRegenerate}
                onStartEdit={setEditingMessageId}
                onCancelEdit={() => setEditingMessageId(null)}
                onSubmitEdit={guardedEdit}
                onNavigate={closeSidebar}
                runs={runs}
                isHeld={isHeld}
                onRunBuild={runBuild}
                onCancelBuild={cancelBuild}
                onRenameClasses={renameClasses}
                interview={{
                  onAnswer: answerInterview,
                  onReopen: reopenInterview,
                  onConfirm: confirmInterview,
                  onCancel: cancelBuild,
                  onQueueTraining: queueTrainingFor,
                  onRetryVocabulary: (message) => reopenInterview(message, 'objects'),
                  // Route by the model's OWN type — a detection import opened
                  // in the classification studio reads a different collection
                  // and renders empty.
                  onOpenStudio: (outcome, navigate) => {
                    const route = labelingRouteFor(modelTypeOf(outcome?.modelDoc));
                    const state = toLabelingState(outcome?.modelDoc, outcome?.versionId);
                    if (!route || !state) return;
                    navigate(route, { state });
                  },
                }}
              />
            ) : (
              <div className="lf-assistant__welcome-scroll">
                <AssistantWelcome
                  workspace={workspace}
                  workspaceLoading={workspaceLoading}
                  onPick={ask}
                />
              </div>
            )}
          </div>
        </AssistantDropZone>

        <div className="lf-assistant__composer">
          <AssistantComposer
            value={draft}
            onChange={setActiveDraft}
            onSubmit={submit}
            onStop={stop}
            onEditLast={editLast}
            isStreaming={isStreaming}
            includeContext={includeContext}
            onToggleContext={() => setIncludeContext(!includeContext)}
            contextAvailable={!!workspace}
            onOpenShortcuts={() => setShortcutsOpen(true)}
            attachment={attachment}
            onAttachFile={attach}
            onRemoveAttachment={removeAttachment}
            seeds={seeds}
          />
        </div>
      </main>

      {contextPanelOpen && (
        <div className="lf-assistant__context">
          <WorkspaceContextPanel
            workspace={workspace}
            loading={workspaceLoading}
            includeContext={includeContext}
            onToggleContext={setIncludeContext}
            onRefresh={loadWorkspace}
            onAsk={ask}
            onClose={() => setContextPanelOpen(false)}
          />
        </div>
      )}

      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
};

export default Assistant;
